import type { Language } from '@/i18n';

/**
 * Controller identity shown in the notice. These are placeholders — replace each
 * value with the operator's real legal details before publishing. They are the
 * ONLY edit point for identity; the prose below interpolates them.
 */
const CONTROLLER = {
  name: 'Łukasz Patro',
  address: 'Zawadka',
  nip: '9182168140',
  email: 'lukasz.patro@outlook.com',
  hosting: 'hosting własny',
} as const;

const UPDATED = '2026-06-26';

export type PolicyBlock = { kind: 'p'; text: string } | { kind: 'list'; items: string[] };

export type PolicySection = {
  heading: string;
  blocks: PolicyBlock[];
};

export type PolicyDoc = {
  title: string;
  updatedLabel: string;
  updatedValue: string;
  backLabel: string;
  intro: PolicyBlock[];
  sections: PolicySection[];
};

const pl: PolicyDoc = {
  title: 'Polityka prywatności i klauzula informacyjna',
  updatedLabel: 'Ostatnia aktualizacja',
  updatedValue: UPDATED,
  backLabel: '← Powrót do aplikacji',
  intro: [
    {
      kind: 'p',
      text: 'Niniejszy dokument wyjaśnia, jakie dane osobowe przetwarzamy w tej aplikacji do obsługi faktur w Krajowym Systemie e-Faktur (KSeF), w jakim celu i na jakiej podstawie prawnej, oraz jakie prawa Ci przysługują. Dotyczy zarówno użytkowników aplikacji, jak i kontrahentów (nabywców i sprzedawców) figurujących na fakturach.',
    },
  ],
  sections: [
    {
      heading: 'Administrator danych',
      blocks: [
        {
          kind: 'p',
          text: `Administratorem Twoich danych osobowych jest ${CONTROLLER.name}, ${CONTROLLER.address}, NIP ${CONTROLLER.nip}.`,
        },
        {
          kind: 'p',
          text: `We wszystkich sprawach dotyczących danych osobowych możesz się z nami skontaktować pod adresem e-mail: ${CONTROLLER.email}.`,
        },
      ],
    },
    {
      heading: 'Jakie dane przetwarzamy i w jakim celu',
      blocks: [
        {
          kind: 'p',
          text: 'Użytkownicy aplikacji: adres e-mail, imię i nazwisko (jeśli zostały podane) oraz adres IP, identyfikator sesji i informacje o logowaniu zapisywane w dzienniku zdarzeń. Cel: umożliwienie logowania, świadczenie usługi i zapewnienie bezpieczeństwa konta. Podstawa: art. 6 ust. 1 lit. b oraz f RODO (wykonanie usługi oraz prawnie uzasadniony interes polegający na zabezpieczeniu dostępu).',
        },
        {
          kind: 'p',
          text: 'Kontrahenci (nabywcy i sprzedawcy): dane identyfikacyjne, adresowe i kontaktowe figurujące na fakturach oraz w zapisanej liście kontrahentów — nazwa lub imię i nazwisko, NIP lub REGON, adres, a opcjonalnie e-mail i telefon. Cel: wystawianie, odbieranie i przechowywanie faktur. Podstawa: art. 6 ust. 1 lit. c RODO (obowiązek prawny wynikający z ustawy o rachunkowości, ustawy o VAT oraz przepisów o KSeF).',
        },
        {
          kind: 'p',
          text: 'Zapisana lista kontrahentów (książka adresowa) ułatwiająca ponowne wystawianie faktur przetwarzana jest na podstawie art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes).',
        },
        {
          kind: 'p',
          text: 'Powyższe dotyczy osób fizycznych. Dane podmiotów będących wyłącznie osobami prawnymi (np. spółek) nie podlegają RODO.',
        },
      ],
    },
    {
      heading: 'Źródło i kategorie danych kontrahentów',
      blocks: [
        { kind: 'p', text: 'Dane kontrahentów będących osobami fizycznymi pozyskujemy z następujących źródeł:' },
        {
          kind: 'list',
          items: [
            'z rejestru REGON prowadzonego przez GUS (wyszukiwanie po numerze NIP),',
            'z faktur otrzymanych za pośrednictwem KSeF,',
            'bezpośrednio od Ciebie lub z zawartych umów.',
          ],
        },
        {
          kind: 'p',
          text: 'Przetwarzane kategorie danych to dane identyfikacyjne (nazwa lub imię i nazwisko, NIP), dane adresowe oraz dane kontaktowe.',
        },
      ],
    },
    {
      heading: 'Odbiorcy danych',
      blocks: [
        { kind: 'p', text: 'Dane osobowe mogą być przekazywane:' },
        {
          kind: 'list',
          items: [
            'Ministerstwu Finansów / Szefowi Krajowej Administracji Skarbowej w ramach Krajowego Systemu e-Faktur (KSeF) — jako odrębnemu, niezależnemu administratorowi, na podstawie obowiązku prawnego;',
            `dostawcy hostingu i infrastruktury IT (${CONTROLLER.hosting}) — jako podmiotowi przetwarzającemu na podstawie umowy powierzenia;`,
            'biuru rachunkowemu obsługującemu rozliczenia — jeśli z takiego korzystamy.',
          ],
        },
      ],
    },
    {
      heading: 'Przekazywanie poza EOG',
      blocks: [
        {
          kind: 'p',
          text: 'Dane przechowujemy na serwerach zlokalizowanych w Europejskim Obszarze Gospodarczym i nie przekazujemy ich poza EOG.',
        },
      ],
    },
    {
      heading: 'Okres przechowywania',
      blocks: [
        {
          kind: 'list',
          items: [
            'Faktury i dokumenty księgowe — przez okres wymagany przepisami, co najmniej 5 lat liczonych zgodnie z ustawą o rachunkowości i Ordynacją podatkową;',
            'Dane logowania i dziennik zdarzeń (w tym adresy IP) — do 18 miesięcy;',
            'Zapisana lista kontrahentów — do czasu jej usunięcia przez Ciebie lub ustania celu przetwarzania.',
          ],
        },
      ],
    },
    {
      heading: 'Twoje prawa',
      blocks: [
        { kind: 'p', text: 'Przysługuje Ci prawo do:' },
        {
          kind: 'list',
          items: [
            'dostępu do swoich danych oraz otrzymania ich kopii,',
            'sprostowania (poprawienia) danych,',
            'usunięcia danych,',
            'ograniczenia przetwarzania,',
            'wniesienia sprzeciwu wobec przetwarzania,',
            'przenoszenia danych.',
          ],
        },
        {
          kind: 'p',
          text: 'Realizacja prawa do usunięcia może być ograniczona obowiązkiem przechowywania faktur przez okres wymagany przepisami (art. 17 ust. 3 lit. b RODO).',
        },
        {
          kind: 'p',
          text: 'Masz również prawo wniesienia skargi do organu nadzorczego — Prezesa Urzędu Ochrony Danych Osobowych (UODO), ul. Stawki 2, 00-193 Warszawa.',
        },
      ],
    },
    {
      heading: 'Pliki cookie',
      blocks: [
        {
          kind: 'p',
          text: 'Aplikacja używa wyłącznie niezbędnego pliku cookie sesji, koniecznego do utrzymania zalogowania. Nie stosujemy plików analitycznych ani marketingowych, dlatego nie wyświetlamy banera zgody na pliki cookie.',
        },
      ],
    },
    {
      heading: 'Zautomatyzowane podejmowanie decyzji',
      blocks: [
        {
          kind: 'p',
          text: 'Nie podejmujemy decyzji opartych wyłącznie na zautomatyzowanym przetwarzaniu, w tym profilowaniu.',
        },
      ],
    },
  ],
};

const en: PolicyDoc = {
  title: 'Privacy policy and information notice',
  updatedLabel: 'Last updated',
  updatedValue: UPDATED,
  backLabel: '← Back to the app',
  intro: [
    {
      kind: 'p',
      text: 'This notice explains what personal data we process in this app for handling invoices in the National e-Invoicing System (KSeF), for what purpose and on what legal basis, and what rights you have. It applies both to users of the app and to the contractors (buyers and sellers) shown on invoices.',
    },
  ],
  sections: [
    {
      heading: 'Data controller',
      blocks: [
        {
          kind: 'p',
          text: `The controller of your personal data is ${CONTROLLER.name}, ${CONTROLLER.address}, tax ID (NIP) ${CONTROLLER.nip}.`,
        },
        {
          kind: 'p',
          text: `For any matter concerning personal data, contact us at: ${CONTROLLER.email}.`,
        },
      ],
    },
    {
      heading: 'What data we process and why',
      blocks: [
        {
          kind: 'p',
          text: 'App users: e-mail address, first and last name (if provided), and the IP address, session identifier and sign-in information recorded in the audit log. Purpose: to enable sign-in, provide the service and keep the account secure. Legal basis: Art. 6(1)(b) and (f) GDPR (performance of the service and the legitimate interest of securing access).',
        },
        {
          kind: 'p',
          text: 'Contractors (buyers and sellers): identification, address and contact data shown on invoices and in the saved-contractor list — company name or first and last name, NIP or REGON, address, and optionally e-mail and phone. Purpose: issuing, receiving and storing invoices. Legal basis: Art. 6(1)(c) GDPR (legal obligation under the Accounting Act, the VAT Act and the KSeF regulations).',
        },
        {
          kind: 'p',
          text: 'The saved-contractor list (address book) that speeds up re-issuing invoices is processed on the basis of Art. 6(1)(f) GDPR (legitimate interest).',
        },
        {
          kind: 'p',
          text: 'This concerns natural persons. Data of entities that are solely legal persons (e.g. companies) is outside the scope of GDPR.',
        },
      ],
    },
    {
      heading: 'Source and categories of contractor data',
      blocks: [
        { kind: 'p', text: 'We obtain the data of contractors who are natural persons from:' },
        {
          kind: 'list',
          items: [
            'the REGON register kept by Statistics Poland (GUS), searched by NIP,',
            'invoices received through KSeF,',
            'directly from you or from concluded contracts.',
          ],
        },
        {
          kind: 'p',
          text: 'The categories processed are identification data (company name or first and last name, NIP), address data and contact data.',
        },
      ],
    },
    {
      heading: 'Recipients',
      blocks: [
        { kind: 'p', text: 'Personal data may be shared with:' },
        {
          kind: 'list',
          items: [
            'the Ministry of Finance / Head of the National Revenue Administration within the National e-Invoicing System (KSeF) — as a separate, independent controller, on the basis of a legal obligation;',
            `the hosting and IT infrastructure provider (${CONTROLLER.hosting}) — as a processor under a data-processing agreement;`,
            'the accounting office handling settlements — if one is used.',
          ],
        },
      ],
    },
    {
      heading: 'Transfers outside the EEA',
      blocks: [
        {
          kind: 'p',
          text: 'Data is stored on servers located within the European Economic Area and we do not transfer it outside the EEA.',
        },
      ],
    },
    {
      heading: 'Retention',
      blocks: [
        {
          kind: 'list',
          items: [
            'Invoices and accounting documents — for the period required by law, at least 5 years counted under the Accounting Act and the Tax Ordinance;',
            'Sign-in data and the audit log (including IP addresses) — up to 18 months;',
            'The saved-contractor list — until you delete it or the purpose ceases.',
          ],
        },
      ],
    },
    {
      heading: 'Your rights',
      blocks: [
        { kind: 'p', text: 'You have the right to:' },
        {
          kind: 'list',
          items: [
            'access your data and obtain a copy,',
            'rectify (correct) your data,',
            'erase your data,',
            'restrict processing,',
            'object to processing,',
            'data portability.',
          ],
        },
        {
          kind: 'p',
          text: 'The right to erasure may be limited by the obligation to retain invoices for the statutory period (Art. 17(3)(b) GDPR).',
        },
        {
          kind: 'p',
          text: 'You also have the right to lodge a complaint with the supervisory authority — the President of the Personal Data Protection Office (UODO), ul. Stawki 2, 00-193 Warsaw, Poland.',
        },
      ],
    },
    {
      heading: 'Cookies',
      blocks: [
        {
          kind: 'p',
          text: 'The app uses only a strictly necessary session cookie required to keep you signed in. We do not use analytics or marketing cookies, so we do not display a cookie-consent banner.',
        },
      ],
    },
    {
      heading: 'Automated decision-making',
      blocks: [
        {
          kind: 'p',
          text: 'We do not make decisions based solely on automated processing, including profiling.',
        },
      ],
    },
  ],
};

export const PRIVACY_POLICY: Record<Language, PolicyDoc> = { en, pl };
